import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import IntelexFrame from "../intelex/IntelexFrame";
import ArrowIcon from "../intelex/ArrowIcon";
import { confirmAction } from "../intelex/ConfirmationDialog";
import ReportField, { REPORT_FIELD_TYPES } from "../intelex/ReportField";
import {
  dataItem,
  dataList,
  displayDate,
  intelexRequest,
  reportDetailPath,
  vamsysPirepReviewUrl,
} from "../intelex/api";

let uniqueSequence = 0;
const uniqueId = (prefix) => {
  uniqueSequence += 1;
  return `${prefix}_${Date.now().toString(36)}_${uniqueSequence.toString(36)}`;
};
const optionTypes = new Set(["dropdown", "radio"]);
const widthOptions = [
  { value: 12, label: "Full width", fraction: "1/1", percent: "100%" },
  { value: 9, label: "Three quarters", fraction: "3/4", percent: "75%" },
  { value: 8, label: "Two thirds", fraction: "2/3", percent: "67%" },
  { value: 6, label: "Half width", fraction: "1/2", percent: "50%" },
  { value: 4, label: "One third", fraction: "1/3", percent: "33%" },
  { value: 3, label: "Quarter width", fraction: "1/4", percent: "25%" },
];
const typeName = (type) =>
  REPORT_FIELD_TYPES.find(([value]) => value === type)?.[1] || "Field";
const reviewName = (value) =>
  ({
    pending: "Pending review",
    accepted: "Accepted",
    rejected: "Rejected",
    invalidated: "PIREP invalidated",
  })[value] || "Pending review";

function freshField(type = "text") {
  return {
    id: uniqueId("field"),
    type,
    label: "",
    help: "",
    placeholder: "",
    required: false,
    width: 12,
    options: optionTypes.has(type)
      ? [{ label: "Option 1", value: "option_1" }]
      : [],
    ...(type === "image" ? { maxFiles: 1 } : {}),
  };
}
function freshSection() {
  return {
    id: uniqueId("section"),
    title: "New section",
    description: "",
    fields: [freshField()],
  };
}
function freshTemplate() {
  return {
    title: "",
    code: "",
    description: "",
    category: "Safety",
    status: "draft",
    requiresPirep: false,
    changeNote: "",
    sections: [freshSection()],
  };
}
function parsedSections(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const result = JSON.parse(value);
      return Array.isArray(result) ? result : [];
    } catch (_error) {
      return [];
    }
  }
  return [];
}
function normalizeTemplate(template) {
  const sections = parsedSections(
    template?.sections || template?.schema?.sections,
  ).map((section) => ({
    ...section,
    id: section.id || uniqueId("section"),
    fields: Array.isArray(section.fields)
      ? section.fields.map((field) => {
          const type = field.type === "select" ? "dropdown" : field.type;
          return {
            ...freshField(type),
            ...field,
            type,
            id: field.id || uniqueId("field"),
            width: Number(field.width) || 12,
            options: Array.isArray(field.options)
              ? field.options.map((option) =>
                  typeof option === "string"
                    ? { label: option, value: option }
                    : option,
                )
              : [],
          };
        })
      : [],
  }));
  return {
    ...freshTemplate(),
    ...template,
    changeNote: "",
    sections: sections.length ? sections : [freshSection()],
  };
}
function moveItem(items, index, direction) {
  const target = index + direction;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item);
  return next;
}
const slugCode = (value) =>
  value
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

function Modal({ title, subtitle, onClose, children, wide = false }) {
  useEffect(() => {
    const close = (event) => {
      if (event.key === "Escape") onClose();
    };
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", close);
    return () => {
      document.body.style.overflow = previous;
      window.removeEventListener("keydown", close);
    };
  }, [onClose]);
  return (
    <div
      className="intelex-builder-modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <section
        className={`intelex-builder-modal${wide ? " is-wide" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button type="button" aria-label="Close editor" onClick={onClose}>
            Close
          </button>
        </header>
        {children}
      </section>
    </div>
  );
}

function OptionsEditor({ options, onChange }) {
  const rows = Array.isArray(options) ? options : [];
  const change = (index, key, value) =>
    onChange(
      rows.map((option, position) =>
        position === index ? { ...option, [key]: value } : option,
      ),
    );
  return (
    <div className="intelex-modal-options">
      <div>
        <strong>Available choices</strong>
        <button
          type="button"
          onClick={() =>
            onChange([
              ...rows,
              {
                label: `Option ${rows.length + 1}`,
                value: `option_${rows.length + 1}`,
              },
            ])
          }
        >
          Add choice
        </button>
      </div>
      {rows.map((option, index) => (
        <article key={index}>
          <span>{index + 1}</span>
          <label>
            <small>Displayed label</small>
            <input
              value={option.label || ""}
              onChange={(event) => change(index, "label", event.target.value)}
            />
          </label>
          <label>
            <small>Stored value</small>
            <input
              value={option.value ?? ""}
              onChange={(event) => change(index, "value", event.target.value)}
            />
          </label>
          <div>
            <button
              type="button"
              disabled={index === 0}
              onClick={() => onChange(moveItem(rows, index, -1))}
            >
              Up
            </button>
            <button
              type="button"
              disabled={index === rows.length - 1}
              onClick={() => onChange(moveItem(rows, index, 1))}
            >
              Down
            </button>
            <button
              type="button"
              className="danger"
              disabled={rows.length === 1}
              onClick={() =>
                onChange(rows.filter((_, position) => position !== index))
              }
            >
              Remove
            </button>
          </div>
        </article>
      ))}
    </div>
  );
}
function WidthSelector({ value, onChange }) {
  return (
    <fieldset className="intelex-width-selector">
      <legend>Displayed width</legend>
      <p>
        The blue area shows how much of a 12-column report row the field will
        occupy.
      </p>
      <div>
        {widthOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            className={Number(value) === option.value ? "selected" : ""}
            onClick={() => onChange(option.value)}
            aria-pressed={Number(value) === option.value}
          >
            <span className="intelex-width-diagram">
              <i style={{ width: option.percent }} />
            </span>
            <strong>{option.label}</strong>
            <small>{option.fraction} of a row</small>
          </button>
        ))}
      </div>
    </fieldset>
  );
}
function FieldEditor({ field, onCancel, onSave }) {
  const [draft, setDraft] = useState(() => ({
    ...field,
    options: [...(field.options || [])],
  }));
  const [error, setError] = useState("");
  const set = (key, value) =>
    setDraft((current) => ({ ...current, [key]: value }));
  const changeType = (type) =>
    setDraft((current) => ({
      ...current,
      type,
      options: optionTypes.has(type)
        ? current.options?.length
          ? current.options
          : [{ label: "Option 1", value: "option_1" }]
        : [],
      ...(type === "image" ? { maxFiles: Number(current.maxFiles) || 1 } : {}),
    }));
  const submit = (event) => {
    event.preventDefault();
    if (!String(draft.label || "").trim()) {
      setError("Enter the label pilots will see.");
      return;
    }
    if (
      optionTypes.has(draft.type) &&
      !(draft.options || []).some((option) => String(option.label || "").trim())
    ) {
      setError("Add at least one displayed choice.");
      return;
    }
    onSave(draft);
  };
  return (
    <form className="intelex-modal-form" onSubmit={submit}>
      <div className="intelex-type-selector">
        <strong>Element type</strong>
        <div>
          {REPORT_FIELD_TYPES.map(([value, label]) => (
            <button
              type="button"
              key={value}
              className={draft.type === value ? "selected" : ""}
              onClick={() => changeType(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className="intelex-modal-form-grid">
        <label>
          <span>Field label</span>
          <input
            autoFocus
            required
            maxLength="180"
            value={draft.label || ""}
            onChange={(event) => set("label", event.target.value)}
            placeholder="What information should the pilot provide?"
          />
        </label>
        <label>
          <span>Placeholder</span>
          <input
            maxLength="200"
            value={draft.placeholder || ""}
            onChange={(event) => set("placeholder", event.target.value)}
            placeholder="Example shown inside the field"
          />
        </label>
        <label className="wide">
          <span>Help text</span>
          <textarea
            rows="3"
            maxLength="500"
            value={draft.help || ""}
            onChange={(event) => set("help", event.target.value)}
            placeholder="Optional instructions shown below the label"
          />
        </label>
        <label className="intelex-modal-checkbox wide">
          <input
            type="checkbox"
            checked={Boolean(draft.required)}
            onChange={(event) => set("required", event.target.checked)}
          />
          <span>Answer is required</span>
        </label>
      </div>
      <WidthSelector
        value={draft.width}
        onChange={(value) => set("width", value)}
      />
      {draft.type === "number" && (
        <div className="intelex-modal-rules">
          <strong>Number rules</strong>
          <label>
            <span>Minimum</span>
            <input
              type="number"
              step="any"
              value={draft.min ?? ""}
              onChange={(event) =>
                set(
                  "min",
                  event.target.value === "" ? null : Number(event.target.value),
                )
              }
            />
          </label>
          <label>
            <span>Maximum</span>
            <input
              type="number"
              step="any"
              value={draft.max ?? ""}
              onChange={(event) =>
                set(
                  "max",
                  event.target.value === "" ? null : Number(event.target.value),
                )
              }
            />
          </label>
          <label>
            <span>Step</span>
            <input
              type="number"
              min="0.000001"
              step="any"
              value={draft.step ?? ""}
              onChange={(event) =>
                set(
                  "step",
                  event.target.value === "" ? null : Number(event.target.value),
                )
              }
            />
          </label>
        </div>
      )}
      {draft.type === "image" && (
        <div className="intelex-modal-rules">
          <strong>Image rules</strong>
          <label>
            <span>Maximum images</span>
            <select
              value={Number(draft.maxFiles) || 1}
              onChange={(event) => set("maxFiles", Number(event.target.value))}
            >
              {[1, 2, 3, 4, 5].map((count) => (
                <option key={count} value={count}>
                  {count}
                </option>
              ))}
            </select>
          </label>
          <p>JPEG and PNG files up to 5 MB are supported.</p>
        </div>
      )}
      {optionTypes.has(draft.type) && (
        <OptionsEditor
          options={draft.options}
          onChange={(options) => set("options", options)}
        />
      )}
      {error && (
        <div className="intelex-alert" role="alert">
          {error}
        </div>
      )}
      <footer>
        <button
          type="button"
          className="intelex-secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className="intelex-primary-button">
          Apply changes
        </button>
      </footer>
    </form>
  );
}
function SectionEditor({ section, onCancel, onSave }) {
  const [draft, setDraft] = useState(() => ({ ...section }));
  return (
    <form
      className="intelex-modal-form"
      onSubmit={(event) => {
        event.preventDefault();
        if (String(draft.title || "").trim()) onSave(draft);
      }}
    >
      <div className="intelex-modal-form-grid one-column">
        <label>
          <span>Section title</span>
          <input
            autoFocus
            required
            maxLength="160"
            value={draft.title || ""}
            onChange={(event) =>
              setDraft((current) => ({ ...current, title: event.target.value }))
            }
          />
        </label>
        <label>
          <span>Instructions</span>
          <textarea
            rows="5"
            maxLength="1000"
            value={draft.description || ""}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
            placeholder="Optional instructions displayed below the section title"
          />
        </label>
      </div>
      <footer>
        <button
          type="button"
          className="intelex-secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className="intelex-primary-button">
          Apply changes
        </button>
      </footer>
    </form>
  );
}
function FormSettingsEditor({ form, onCancel, onSave }) {
  const [draft, setDraft] = useState(() => ({ ...form }));
  const set = (key, value) =>
    setDraft((current) => ({ ...current, [key]: value }));
  return (
    <form
      className="intelex-modal-form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({ ...draft, code: slugCode(draft.code) });
      }}
    >
      <div className="intelex-modal-form-grid">
        <label>
          <span>Report name</span>
          <input
            autoFocus
            required
            maxLength="180"
            value={draft.title || ""}
            onChange={(event) =>
              setDraft((current) => ({
                ...current,
                title: event.target.value,
                code: current.code || slugCode(event.target.value),
              }))
            }
          />
        </label>
        <label>
          <span>Form code</span>
          <input
            required
            maxLength="40"
            value={draft.code || ""}
            onChange={(event) => set("code", slugCode(event.target.value))}
          />
        </label>
        <label>
          <span>Category</span>
          <input
            required
            maxLength="80"
            value={draft.category || ""}
            onChange={(event) => set("category", event.target.value)}
          />
        </label>
        <label>
          <span>Availability</span>
          <select
            value={draft.status || "draft"}
            onChange={(event) => set("status", event.target.value)}
          >
            <option value="draft">Draft - admins only</option>
            <option value="published">Published - available to pilots</option>
          </select>
        </label>
        <label className="wide">
          <span>Description</span>
          <textarea
            rows="4"
            maxLength="2000"
            value={draft.description || ""}
            onChange={(event) => set("description", event.target.value)}
          />
        </label>
        <label className="intelex-modal-checkbox wide">
          <input
            type="checkbox"
            checked={Boolean(draft.requiresPirep)}
            onChange={(event) => set("requiresPirep", event.target.checked)}
          />
          <span>
            <strong>Require a linked PIREP</strong>
            <small>Pilots must choose one of their own flight reports.</small>
          </span>
        </label>
      </div>
      <footer>
        <button
          type="button"
          className="intelex-secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
        <button type="submit" className="intelex-primary-button">
          Apply settings
        </button>
      </footer>
    </form>
  );
}
function ElementPicker({ onCancel, onPick }) {
  const descriptions = {
    text: "Single-line answer",
    textarea: "Detailed written response",
    dropdown: "List of selectable options",
    date: "Calendar date only",
    time: "Time with minutes",
    datetime: "Date and exact time",
    number: "Numeric answer and limits",
    radio: "Visible single-choice options",
    yes_no: "Yes or no choice",
    checkbox: "Required confirmation",
    airport: "Four-letter ICAO code",
    image: "Private image evidence",
  };
  return (
    <div className="intelex-element-picker">
      <p>Select what should appear next in this section.</p>
      <div>
        {REPORT_FIELD_TYPES.map(([type, label]) => (
          <button type="button" key={type} onClick={() => onPick(type)}>
            <strong>{label}</strong>
            <span>{descriptions[type]}</span>
          </button>
        ))}
      </div>
      <footer>
        <button
          type="button"
          className="intelex-secondary-button"
          onClick={onCancel}
        >
          Cancel
        </button>
      </footer>
    </div>
  );
}

function InlineFieldPreview({ field, onChange }) {
  const options = Array.isArray(field.options) ? field.options : [];
  const set = (key, value) => onChange({ ...field, [key]: value });
  const changeOption = (index, label) =>
    set(
      "options",
      options.map((option, position) =>
        position === index ? { ...option, label } : option,
      ),
    );
  return (
    <div className="intelex-inline-field-editor">
      <div className="intelex-inline-field-label-row">
        <input
          className="intelex-inline-field-label"
          maxLength="180"
          aria-label="Field label"
          value={field.label || ""}
          placeholder="Untitled field"
          onChange={(event) => set("label", event.target.value)}
        />
        <label>
          <input
            type="checkbox"
            checked={Boolean(field.required)}
            onChange={(event) => set("required", event.target.checked)}
          />{" "}
          Required
        </label>
      </div>
      <input
        className="intelex-inline-field-help"
        maxLength="500"
        aria-label="Field help text"
        value={field.help || ""}
        placeholder="Optional help text"
        onChange={(event) => set("help", event.target.value)}
      />
      {optionTypes.has(field.type) ? (
        <div className="intelex-inline-field-options">
          {options.map((option, index) => (
            <label key={`${option.value}-${index}`}>
              <i />
              <input
                maxLength="300"
                aria-label={`Option ${index + 1}`}
                value={option.label || ""}
                onChange={(event) => changeOption(index, event.target.value)}
              />
            </label>
          ))}
        </div>
      ) : (
        <div className="intelex-inline-report-preview">
          <ReportField
            field={{ ...field, label: "Preview", help: "", width: 12 }}
            value={field.type === "checkbox" ? false : ""}
            onChange={() => {}}
            disabled
          />
        </div>
      )}
      {["text", "textarea", "number", "airport"].includes(field.type) && (
        <input
          className="intelex-inline-placeholder"
          maxLength="200"
          aria-label="Field placeholder"
          value={field.placeholder || ""}
          placeholder="Placeholder text"
          onChange={(event) => set("placeholder", event.target.value)}
        />
      )}
    </div>
  );
}

function VisualDesigner({
  form,
  setForm,
  state,
  validationErrors,
  onCancel,
  onSave,
}) {
  const [modal, setModal] = useState(null);
  const [openFields, setOpenFields] = useState({});
  const fieldDisclosureKey = (sectionIndex, fieldIndex) =>
    `${sectionIndex}:${fieldIndex}`;
  const toggleField = (sectionIndex, fieldIndex) => {
    const key = fieldDisclosureKey(sectionIndex, fieldIndex);
    setOpenFields((current) => ({ ...current, [key]: !current[key] }));
  };
  const replaceSection = (sectionIndex, section) =>
    setForm((current) => ({
      ...current,
      sections: current.sections.map((item, index) =>
        index === sectionIndex ? section : item,
      ),
    }));
  const replaceField = (sectionIndex, fieldIndex, field) =>
    setForm((current) => ({
      ...current,
      sections: current.sections.map((section, index) =>
        index === sectionIndex
          ? {
              ...section,
              fields: section.fields.map((item, position) =>
                position === fieldIndex ? field : item,
              ),
            }
          : section,
      ),
    }));
  const removeField = useCallback(
    (sectionIndex, fieldIndex) =>
      setForm((current) => ({
        ...current,
        sections: current.sections.map((section, index) =>
          index === sectionIndex
            ? {
                ...section,
                fields: section.fields.filter(
                  (_, position) => position !== fieldIndex,
                ),
              }
            : section,
        ),
      })),
    [setForm],
  );
  const cancelModal = useCallback(() => {
    if (modal?.created === "field")
      removeField(modal.sectionIndex, modal.fieldIndex);
    if (modal?.created === "section")
      setForm((current) => ({
        ...current,
        sections: current.sections.filter(
          (_, index) => index !== modal.sectionIndex,
        ),
      }));
    setModal(null);
  }, [modal, removeField, setForm]);
  const chooseElement = (type) => {
    const { sectionIndex, insertAt: fieldIndex } = modal;
    const field = freshField(type);
    setForm((current) => ({
      ...current,
      sections: current.sections.map((section, index) =>
        index === sectionIndex
          ? {
              ...section,
              fields: [
                ...section.fields.slice(0, fieldIndex),
                field,
                ...section.fields.slice(fieldIndex),
              ],
            }
          : section,
      ),
    }));
    setModal({ kind: "field", sectionIndex, fieldIndex, created: "field" });
  };
  const addSection = (insertAt) => {
    const section = freshSection();
    setForm((current) => ({
      ...current,
      sections: [
        ...current.sections.slice(0, insertAt),
        section,
        ...current.sections.slice(insertAt),
      ],
    }));
    setModal({ kind: "section", sectionIndex: insertAt, created: "section" });
  };
  const currentSection =
    modal?.sectionIndex !== undefined
      ? form.sections[modal.sectionIndex]
      : null;
  const currentField =
    currentSection && modal?.fieldIndex !== undefined
      ? currentSection.fields[modal.fieldIndex]
      : null;
  return (
    <div className="intelex-main intelex-builder-page intelex-visual-builder-page">
      <header className="intelex-builder-heading">
        <div>
          <button
            type="button"
            className="intelex-back-button"
            onClick={onCancel}
          >
            Back to report forms
          </button>
          <h1>{form.id ? `Edit ${form.title}` : "Create report form"}</h1>
          <p>
            Edit visible content directly or open advanced settings for the
            complete configuration.
          </p>
        </div>
        <div>
          <button
            type="button"
            className="intelex-secondary-button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="intelex-primary-button"
            disabled={state.saving}
            onClick={onSave}
          >
            {state.saving
              ? "Saving..."
              : form.status === "published"
                ? "Save and publish"
                : "Save draft"}
          </button>
        </div>
      </header>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      <section className="intelex-visual-form-identity">
        <div>
          <span>
            {form.code || "FORM CODE"} / {form.category || "Category"}
          </span>
          <input
            className="intelex-inline-heading"
            maxLength="180"
            aria-label="Report form title"
            value={form.title || ""}
            placeholder="Untitled report form"
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                title: event.target.value,
                code: current.code || slugCode(event.target.value),
              }))
            }
          />
          <textarea
            className="intelex-inline-description"
            rows="2"
            maxLength="2000"
            aria-label="Report form description"
            value={form.description || ""}
            placeholder="Add a form description"
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                description: event.target.value,
              }))
            }
          />
        </div>
        <dl>
          <div>
            <dt>Availability</dt>
            <dd>{form.status === "published" ? "Published" : "Draft"}</dd>
          </div>
          <div>
            <dt>Linked PIREP</dt>
            <dd>{form.requiresPirep ? "Required" : "Not required"}</dd>
          </div>
        </dl>
        <button type="button" onClick={() => setModal({ kind: "settings" })}>
          Advanced form settings
        </button>
      </section>
      {form.id && (
        <label className="intelex-revision-note">
          <span>Revision note</span>
          <input
            value={form.changeNote || ""}
            maxLength="1000"
            onChange={(event) =>
              setForm((current) => ({
                ...current,
                changeNote: event.target.value,
              }))
            }
            placeholder="Describe what changed in this revision"
          />
        </label>
      )}
      <div className="intelex-visual-form-canvas">
        {form.requiresPirep && (
          <section className="intelex-form-section intelex-visual-section is-system">
            <div className="intelex-form-section-heading">
              <span>01</span>
              <div>
                <h2>Related flight report</h2>
                <p>The pilot must select one of their own PIREPs.</p>
              </div>
              <small>Automatic section</small>
            </div>
            <div className="intelex-form-grid">
              <div className="intelex-field intelex-width-12">
                <label className="intelex-field-label">
                  PIREP <span>*</span>
                </label>
                <select disabled>
                  <option>Select a flight from your PIREP history</option>
                </select>
              </div>
            </div>
          </section>
        )}
        {form.sections.map((section, sectionIndex) => (
          <div
            className="intelex-visual-section-wrap"
            key={section.id || sectionIndex}
          >
            <section className="intelex-form-section intelex-visual-section">
              <div className="intelex-form-section-heading">
                <span>
                  {String(sectionIndex + (form.requiresPirep ? 2 : 1)).padStart(
                    2,
                    "0",
                  )}
                </span>
                <div>
                  <input
                    className="intelex-inline-section-title"
                    maxLength="160"
                    aria-label={`Section ${sectionIndex + 1} title`}
                    value={section.title || ""}
                    onChange={(event) =>
                      replaceSection(sectionIndex, {
                        ...section,
                        title: event.target.value,
                      })
                    }
                  />
                  <textarea
                    className="intelex-inline-section-description"
                    rows="2"
                    maxLength="1000"
                    aria-label={`Section ${sectionIndex + 1} instructions`}
                    value={section.description || ""}
                    placeholder="Optional section instructions"
                    onChange={(event) =>
                      replaceSection(sectionIndex, {
                        ...section,
                        description: event.target.value,
                      })
                    }
                  />
                </div>
                <div className="intelex-visual-section-actions">
                  <button
                    type="button"
                    onClick={() => setModal({ kind: "section", sectionIndex })}
                  >
                    Advanced settings
                  </button>
                  <button
                    type="button"
                    disabled={sectionIndex === 0}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        sections: moveItem(current.sections, sectionIndex, -1),
                      }))
                    }
                  >
                    Move up
                  </button>
                  <button
                    type="button"
                    disabled={sectionIndex === form.sections.length - 1}
                    onClick={() =>
                      setForm((current) => ({
                        ...current,
                        sections: moveItem(current.sections, sectionIndex, 1),
                      }))
                    }
                  >
                    Move down
                  </button>
                  <button
                    type="button"
                    className="danger"
                    onClick={async () => {
                      if (
                        await confirmAction({
                          title: "Remove form section",
                          message: `Remove ${section.title || "this section"}? Every field inside it will be removed from this draft.`,
                          confirmLabel: "Remove section",
                          tone: "danger",
                        })
                      )
                        setForm((current) => ({
                          ...current,
                          sections: current.sections.filter(
                            (_, index) => index !== sectionIndex,
                          ),
                        }));
                    }}
                  >
                    Remove
                  </button>
                </div>
              </div>
              <div className="intelex-form-grid intelex-visual-field-grid">
                {(section.fields || []).map((field, fieldIndex) => (
                  <div
                    className={`intelex-visual-field-card intelex-width-${Number(field.width) || 12}`}
                    key={field.id || fieldIndex}
                  >
                    <div className="intelex-visual-field-toolbar">
                      <button
                        type="button"
                        className="intelex-editor-disclosure"
                        aria-expanded={Boolean(
                          openFields[
                            fieldDisclosureKey(sectionIndex, fieldIndex)
                          ],
                        )}
                        onClick={() => toggleField(sectionIndex, fieldIndex)}
                      >
                        <span>
                          <strong>{field.label || "Untitled field"}</strong>
                          <small>
                            {typeName(field.type)} ·{" "}
                            {widthOptions.find(
                              (option) => option.value === Number(field.width),
                            )?.label || `${field.width}/12 width`}
                          </small>
                        </span>
                        <ArrowIcon direction="down" />
                      </button>
                      {openFields[
                        fieldDisclosureKey(sectionIndex, fieldIndex)
                      ] && (
                        <div>
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                kind: "field",
                                sectionIndex,
                                fieldIndex,
                              })
                            }
                          >
                            Advanced edit
                          </button>
                          <button
                            type="button"
                            disabled={fieldIndex === 0}
                            onClick={() =>
                              replaceSection(sectionIndex, {
                                ...section,
                                fields: moveItem(
                                  section.fields,
                                  fieldIndex,
                                  -1,
                                ),
                              })
                            }
                          >
                            Left
                          </button>
                          <button
                            type="button"
                            disabled={fieldIndex === section.fields.length - 1}
                            onClick={() =>
                              replaceSection(sectionIndex, {
                                ...section,
                                fields: moveItem(section.fields, fieldIndex, 1),
                              })
                            }
                          >
                            Right
                          </button>
                          <button
                            type="button"
                            className="danger"
                            onClick={async () => {
                              if (
                                await confirmAction({
                                  title: "Remove form field",
                                  message: `Remove ${field.label || "this field"} from this draft?`,
                                  confirmLabel: "Remove field",
                                  tone: "danger",
                                })
                              )
                                removeField(sectionIndex, fieldIndex);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="intelex-visual-field-content">
                      {openFields[
                        fieldDisclosureKey(sectionIndex, fieldIndex)
                      ] ? (
                        <InlineFieldPreview
                          field={field}
                          onChange={(updated) =>
                            replaceField(sectionIndex, fieldIndex, updated)
                          }
                        />
                      ) : (
                        <div className="intelex-field-display-preview">
                          <ReportField
                            field={{ ...field, width: 12 }}
                            value={field.type === "checkbox" ? false : ""}
                            onChange={() => {}}
                            disabled
                          />
                        </div>
                      )}
                    </div>
                    <button
                      type="button"
                      className="intelex-inline-add"
                      onClick={() =>
                        setModal({
                          kind: "picker",
                          sectionIndex,
                          insertAt: fieldIndex + 1,
                        })
                      }
                    >
                      <span>+</span> Add element after this field
                    </button>
                  </div>
                ))}
                {!(section.fields || []).length && (
                  <button
                    type="button"
                    className="intelex-empty-field-add intelex-width-12"
                    onClick={() =>
                      setModal({ kind: "picker", sectionIndex, insertAt: 0 })
                    }
                  >
                    + Add the first element
                  </button>
                )}
              </div>
            </section>
            <button
              type="button"
              className="intelex-add-section-between"
              onClick={() => addSection(sectionIndex + 1)}
            >
              <span>+</span> Add a section below
            </button>
          </div>
        ))}
        {!form.sections.length && (
          <button
            type="button"
            className="intelex-add-section-between first"
            onClick={() => addSection(0)}
          >
            <span>+</span> Add the first section
          </button>
        )}
      </div>
      {validationErrors.length > 0 && (
        <div className="intelex-builder-checklist">
          <strong>Before this form can be saved</strong>
          <ul>
            {validationErrors.map((error, index) => (
              <li key={`${error}-${index}`}>{error}</li>
            ))}
          </ul>
        </div>
      )}
      <div className="intelex-visual-builder-actions">
        <span>
          {form.sections.length} sections /{" "}
          {form.sections.reduce(
            (total, section) => total + (section.fields?.length || 0),
            0,
          )}{" "}
          fields
        </span>
        <div>
          <button
            type="button"
            className="intelex-secondary-button"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            type="button"
            className="intelex-primary-button"
            disabled={state.saving}
            onClick={onSave}
          >
            {state.saving ? "Saving..." : "Save form"}
          </button>
        </div>
      </div>
      {modal?.kind === "settings" && (
        <Modal
          title="Form settings"
          subtitle="Change the form identity, availability, and PIREP requirement."
          onClose={cancelModal}
          wide
        >
          <FormSettingsEditor
            form={form}
            onCancel={cancelModal}
            onSave={(settings) => {
              setForm((current) => ({ ...current, ...settings }));
              setModal(null);
            }}
          />
        </Modal>
      )}
      {modal?.kind === "section" && currentSection && (
        <Modal
          title="Edit section"
          subtitle="This heading and instruction text are shown to pilots."
          onClose={cancelModal}
        >
          <SectionEditor
            key={currentSection.id}
            section={currentSection}
            onCancel={cancelModal}
            onSave={(section) => {
              replaceSection(modal.sectionIndex, section);
              setModal(null);
            }}
          />
        </Modal>
      )}
      {modal?.kind === "field" && currentField && (
        <Modal
          title={`Edit ${typeName(currentField.type)}`}
          subtitle="Configure the element exactly as it should appear to pilots."
          onClose={cancelModal}
          wide
        >
          <FieldEditor
            key={currentField.id}
            field={currentField}
            onCancel={cancelModal}
            onSave={(field) => {
              replaceField(modal.sectionIndex, modal.fieldIndex, field);
              setModal(null);
            }}
          />
        </Modal>
      )}
      {modal?.kind === "picker" && (
        <Modal
          title="Add an element"
          subtitle="The new element will be inserted at this exact position."
          onClose={cancelModal}
          wide
        >
          <ElementPicker onCancel={cancelModal} onPick={chooseElement} />
        </Modal>
      )}
    </div>
  );
}

function SortButton({ value, sort, direction, onChange, children }) {
  const active = sort === value;
  return (
    <button
      type="button"
      className={active ? "active" : ""}
      onClick={() =>
        onChange(value, active && direction === "asc" ? "desc" : "asc")
      }
    >
      {children}
      {active && <ArrowIcon direction={direction === "asc" ? "up" : "down"} />}
    </button>
  );
}
function ReportLibrary({ templates }) {
  const [reports, setReports] = useState([]);
  const [filters, setFilters] = useState({
    q: "",
    review: "",
    state: "",
    template: "",
    sort: "submitted",
    direction: "desc",
    page: 1,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });
  const [state, setState] = useState({ loading: true, error: "" });
  useEffect(() => {
    let current = true;
    const timer = window.setTimeout(
      async () => {
        setState({ loading: true, error: "" });
        const params = new URLSearchParams({
          scope: "all",
          page: String(filters.page),
          page_size: "50",
          sort: filters.sort,
          direction: filters.direction,
        });
        if (filters.q.trim()) params.set("q", filters.q.trim());
        if (filters.review) params.set("review_status", filters.review);
        if (filters.state) params.set("state", filters.state);
        if (filters.template) params.set("template_id", filters.template);
        try {
          const payload = await intelexRequest(`/api/portal/reports?${params}`);
          if (!current) return;
          setReports(dataList(payload));
          setPagination(
            payload.pagination || {
              page: 1,
              total: dataList(payload).length,
              totalPages: 1,
            },
          );
          setState({ loading: false, error: "" });
        } catch (error) {
          if (current)
            setState({
              loading: false,
              error: error.message || "Reports could not be loaded.",
            });
        }
      },
      filters.q ? 250 : 0,
    );
    return () => {
      current = false;
      window.clearTimeout(timer);
    };
  }, [filters]);
  const setFilter = (key, value) =>
    setFilters((current) => ({
      ...current,
      [key]: value,
      page: key === "page" ? value : 1,
    }));
  const setSort = (sort, direction) =>
    setFilters((current) => ({ ...current, sort, direction, page: 1 }));
  return (
    <section className="intelex-panel intelex-report-library">
      <div className="intelex-panel-heading">
        <div>
          <h2>All submitted reports</h2>
          <span>{pagination.total} reports</span>
        </div>
      </div>
      <div className="intelex-report-library-filters">
        <label>
          <span>Search</span>
          <input
            type="search"
            value={filters.q}
            onChange={(event) => setFilter("q", event.target.value)}
            placeholder="Event number, pilot, form or PIREP"
          />
        </label>
        <label>
          <span>Form</span>
          <select
            value={filters.template}
            onChange={(event) => setFilter("template", event.target.value)}
          >
            <option value="">All forms</option>
            {templates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.title}
              </option>
            ))}
          </select>
        </label>
        <label>
          <span>Review result</span>
          <select
            value={filters.review}
            onChange={(event) => setFilter("review", event.target.value)}
          >
            <option value="">All results</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="invalidated">PIREP invalidated</option>
          </select>
        </label>
        <label>
          <span>Report state</span>
          <select
            value={filters.state}
            onChange={(event) => setFilter("state", event.target.value)}
          >
            <option value="">Open and closed</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </label>
      </div>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      {state.loading ? (
        <div className="intelex-loading" role="status">
          Loading reports...
        </div>
      ) : reports.length ? (
        <div className="intelex-table-scroll">
          <table className="intelex-table intelex-report-library-table">
            <thead>
              <tr>
                <th>
                  <SortButton
                    value="reference"
                    sort={filters.sort}
                    direction={filters.direction}
                    onChange={setSort}
                  >
                    Event number
                  </SortButton>
                </th>
                <th>
                  <SortButton
                    value="form"
                    sort={filters.sort}
                    direction={filters.direction}
                    onChange={setSort}
                  >
                    Report form
                  </SortButton>
                </th>
                <th>
                  <SortButton
                    value="pilot"
                    sort={filters.sort}
                    direction={filters.direction}
                    onChange={setSort}
                  >
                    Pilot
                  </SortButton>
                </th>
                <th>
                  <SortButton
                    value="submitted"
                    sort={filters.sort}
                    direction={filters.direction}
                    onChange={setSort}
                  >
                    Submitted
                  </SortButton>
                </th>
                <th>Linked PIREP</th>
                <th>
                  <SortButton
                    value="review"
                    sort={filters.sort}
                    direction={filters.direction}
                    onChange={setSort}
                  >
                    Review
                  </SortButton>
                </th>
                <th>Report state</th>
                <th>
                  <span className="sr-only">Open</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {reports.map((report) => (
                <tr key={report.id}>
                  <td>
                    <strong>{report.eventNumber || `#${report.id}`}</strong>
                    {report.isTestSubmission && (
                      <span className="intelex-test-badge">Test</span>
                    )}
                  </td>
                  <td>
                    {report.templateTitle ||
                      report.template?.title ||
                      "Safety report"}
                  </td>
                  <td>
                    {report.submittedBy || "-"}
                    {report.submittedByVamsysUserId && (
                      <small className="intelex-table-note">
                        vAMSYS #{report.submittedByVamsysUserId}
                      </small>
                    )}
                  </td>
                  <td>{displayDate(report.submittedAt)}</td>
                  <td>
                    {vamsysPirepReviewUrl(report.pirepId) ? (
                      <a
                        className="intelex-table-link"
                        href={vamsysPirepReviewUrl(report.pirepId)}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        #{report.pirepId}
                      </a>
                    ) : report.pirepId ? (
                      `#${report.pirepId}`
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>
                    <span
                      className={`intelex-result intelex-result-${report.reviewStatus || "pending"}`}
                    >
                      {reviewName(report.reviewStatus)}
                    </span>
                  </td>
                  <td>{report.status === "closed" ? "Closed" : "Open"}</td>
                  <td>
                    <Link
                      className="intelex-table-link"
                      to={reportDetailPath(report)}
                    >
                      Open
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="intelex-empty">
          <strong>No reports match these filters</strong>
          <p>Try a different search or filter combination.</p>
        </div>
      )}
      <footer className="intelex-library-pagination">
        <span>
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <div>
          <button
            type="button"
            disabled={pagination.page <= 1 || state.loading}
            onClick={() => setFilter("page", pagination.page - 1)}
          >
            <ArrowIcon direction="left" />
            Previous
          </button>
          <button
            type="button"
            disabled={pagination.page >= pagination.totalPages || state.loading}
            onClick={() => setFilter("page", pagination.page + 1)}
          >
            Next
            <ArrowIcon />
          </button>
        </div>
      </footer>
    </section>
  );
}

function TemplateBuilder({ user, isAdmin }) {
  const admin = isAdmin ?? user?.role === "admin";
  const [templates, setTemplates] = useState([]);
  const [form, setForm] = useState(null);
  const [tab, setTab] = useState("forms");
  const [state, setState] = useState({
    loading: true,
    saving: false,
    error: "",
    success: "",
  });
  const loadTemplates = useCallback(async () => {
    try {
      setTemplates(
        dataList(
          await intelexRequest("/api/portal/report-templates?include_all=true"),
        ),
      );
      setState((current) => ({ ...current, loading: false, error: "" }));
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error.message || "Report forms could not be loaded.",
      }));
    }
  }, []);
  useEffect(() => {
    if (admin) loadTemplates();
    else setState((current) => ({ ...current, loading: false }));
  }, [admin, loadTemplates]);
  const validationErrors = useMemo(() => {
    if (!form) return [];
    const errors = [];
    if (!String(form.title || "").trim())
      errors.push("Enter a form title in Form settings.");
    if (!String(form.code || "").trim())
      errors.push("Enter a unique form code in Form settings.");
    if (!String(form.category || "").trim())
      errors.push("Enter a category in Form settings.");
    if (!form.sections.length) errors.push("Add at least one section.");
    form.sections.forEach((section, sectionIndex) => {
      if (!String(section.title || "").trim())
        errors.push(`Section ${sectionIndex + 1} needs a title.`);
      if (!(section.fields || []).length)
        errors.push(
          `${section.title || `Section ${sectionIndex + 1}`} needs at least one field.`,
        );
      (section.fields || []).forEach((field, fieldIndex) => {
        if (!String(field.label || "").trim())
          errors.push(
            `Field ${fieldIndex + 1} in section ${sectionIndex + 1} needs a label.`,
          );
        if (
          optionTypes.has(field.type) &&
          !(field.options || []).some((option) =>
            String(option.label || "").trim(),
          )
        )
          errors.push(
            `${field.label || `Field ${fieldIndex + 1}`} needs at least one choice.`,
          );
      });
    });
    if (form.id && !String(form.changeNote || "").trim())
      errors.push("Describe what changed in this revision.");
    return errors;
  }, [form]);
  const openTemplate = async (id) => {
    setState((current) => ({
      ...current,
      loading: true,
      error: "",
      success: "",
    }));
    try {
      setForm(
        normalizeTemplate(
          dataItem(await intelexRequest(`/api/portal/report-templates/${id}`)),
        ),
      );
      setState((current) => ({ ...current, loading: false }));
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error.message || "The report form could not be opened.",
      }));
    }
  };
  const save = async () => {
    if (validationErrors.length) {
      setState((current) => ({
        ...current,
        error: validationErrors[0],
        success: "",
      }));
      return;
    }
    setState((current) => ({
      ...current,
      saving: true,
      error: "",
      success: "",
    }));
    try {
      const editing = Boolean(form.id);
      const result = dataItem(
        await intelexRequest(
          editing
            ? `/api/portal/report-templates/${form.id}`
            : "/api/portal/report-templates",
          {
            method: editing ? "PUT" : "POST",
            body: { ...form, code: slugCode(form.code) },
          },
        ),
      );
      const title = form.title;
      await loadTemplates();
      setForm(null);
      setState((current) => ({
        ...current,
        saving: false,
        success: `${title} was ${editing ? "updated" : "created"}${result?.revision ? ` as revision ${result.revision}` : ""}.`,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        saving: false,
        error: error.message || "The report form could not be saved.",
      }));
    }
  };
  const archive = async (template) => {
    if (
      !(await confirmAction({
        title: "Archive report form",
        message: `Archive "${template.title}"? Pilots will no longer be able to start this report.`,
        confirmLabel: "Archive form",
        tone: "danger",
      }))
    )
      return;
    try {
      await intelexRequest(`/api/portal/report-templates/${template.id}`, {
        method: "DELETE",
      });
      await loadTemplates();
      setState((current) => ({
        ...current,
        success: `${template.title} was archived.`,
        error: "",
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message || "The report form could not be archived.",
      }));
    }
  };
  const deleteArchived = async (template) => {
    if (
      !(await confirmAction({
        title: "Delete archived form",
        message: `Permanently delete "${template.title}"? Submitted reports will remain available with their original form snapshot. This cannot be undone.`,
        confirmLabel: "Delete permanently",
        tone: "danger",
      }))
    )
      return;
    try {
      await intelexRequest(
        `/api/portal/report-templates/${template.id}/permanent`,
        { method: "DELETE" },
      );
      await loadTemplates();
      setState((current) => ({
        ...current,
        success: `${template.title} was permanently deleted.`,
        error: "",
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        error: error.message || "The archived form could not be deleted.",
      }));
    }
  };
  const duplicate = async (template) => {
    setState((current) => ({
      ...current,
      loading: true,
      error: "",
      success: "",
    }));
    try {
      const copy = dataItem(
        await intelexRequest(
          `/api/portal/report-templates/${template.id}/duplicate`,
          { method: "POST" },
        ),
      );
      await loadTemplates();
      setForm(
        normalizeTemplate(
          dataItem(
            await intelexRequest(`/api/portal/report-templates/${copy.id}`),
          ),
        ),
      );
      setState((current) => ({
        ...current,
        loading: false,
        success: "A draft copy is ready to edit.",
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        loading: false,
        error: error.message || "The report form could not be duplicated.",
      }));
    }
  };
  if (!admin)
    return (
      <div className="intelex-main">
        <div className="intelex-alert" role="alert">
          Administrator access is required.
        </div>
        <Link className="intelex-secondary-button" to="/interep">
          Back to reports
        </Link>
      </div>
    );
  if (form)
    return (
      <VisualDesigner
        form={form}
        setForm={setForm}
        state={state}
        validationErrors={validationErrors}
        onCancel={() => setForm(null)}
        onSave={save}
      />
    );
  return (
    <div className="intelex-main intelex-template-admin-page">
      <header className="intelex-page-heading">
        <div>
          <h1>Report form designer</h1>
          <p>Build report forms and inspect every submitted report.</p>
        </div>
        {tab === "forms" && (
          <button
            className="intelex-primary-button"
            type="button"
            onClick={() => {
              setForm(freshTemplate());
              setState((current) => ({ ...current, error: "", success: "" }));
            }}
          >
            Create report form
          </button>
        )}
      </header>
      {state.error && (
        <div className="intelex-alert" role="alert">
          {state.error}
        </div>
      )}
      {state.success && (
        <div className="intelex-success" role="status">
          {state.success}
        </div>
      )}
      <nav
        className="intelex-admin-library-tabs"
        aria-label="Form designer views"
      >
        <button
          type="button"
          className={tab === "forms" ? "active" : ""}
          onClick={() => setTab("forms")}
        >
          Report forms <span>{templates.length}</span>
        </button>
        <button
          type="button"
          className={tab === "reports" ? "active" : ""}
          onClick={() => setTab("reports")}
        >
          Submitted reports
        </button>
      </nav>
      {tab === "reports" ? (
        <ReportLibrary templates={templates} />
      ) : (
        <section className="intelex-panel">
          <div className="intelex-panel-heading">
            <div>
              <h2>Report forms</h2>
              <span>{templates.length} configured</span>
            </div>
          </div>
          {state.loading ? (
            <div className="intelex-loading" role="status">
              Loading report forms...
            </div>
          ) : templates.length ? (
            <div className="intelex-admin-template-list">
              {templates.map((template) => (
                <article key={template.id}>
                  <div>
                    <h3>{template.title}</h3>
                    <p>{template.description || "No description"}</p>
                  </div>
                  <dl>
                    <div>
                      <dt>Status</dt>
                      <dd>
                        <span
                          className={`intelex-status intelex-status-${template.status}`}
                        >
                          {template.status}
                        </span>
                      </dd>
                    </div>
                    <div>
                      <dt>Revision</dt>
                      <dd>{template.revision || 1}</dd>
                    </div>
                    <div>
                      <dt>Reports</dt>
                      <dd>{template.reportCount || 0}</dd>
                    </div>
                  </dl>
                  <div className="intelex-template-actions">
                    {template.status !== "archived" && (
                      <button
                        type="button"
                        onClick={() => openTemplate(template.id)}
                      >
                        Edit form
                      </button>
                    )}
                    <button type="button" onClick={() => duplicate(template)}>
                      Duplicate
                    </button>
                    {template.status !== "archived" ? (
                      <button
                        type="button"
                        className="danger"
                        onClick={() => archive(template)}
                      >
                        Archive
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="danger"
                        onClick={() => deleteArchived(template)}
                      >
                        Delete permanently
                      </button>
                    )}
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="intelex-empty">
              <strong>No report forms configured</strong>
              <p>Create the first form from a blank layout.</p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}

export default function IntelexTemplateBuilderPage() {
  return (
    <IntelexFrame active="admin" title="Report form designer">
      {({ user, isAdmin }) => <TemplateBuilder user={user} isAdmin={isAdmin} />}
    </IntelexFrame>
  );
}
